import { jsx } from 'react/jsx-runtime';
import { useState, useRef } from 'react';
import { utils } from 'ethers';
import { DynamicError } from '@dynamic-labs/utils';
import { getChainInfo } from '@dynamic-labs/wallet-connector-core';
import '@dynamic-labs/sdk-api';
import '../../config/ApiEndpoint.js';
import '../../shared/logger.js';
import '../../config/LSSuffix.js';
import { getChainIcon } from '../../shared/utils/functions/chain/getChainIcon.js';
import { shortenWalletAddress } from '../../shared/utils/functions/shortenWalletAddress/shortenWalletAddress.js';
import '../../utils/constants/colors.js';
import { formatBigNumber } from '../../shared/utils/functions/formatBigNumber/formatBigNumber.js';
import '@dynamic-labs/iconic';
import '../../context/ViewContext/ViewContext.js';
import '../../shared/consts/index.js';
import '@dynamic-labs/multi-wallet';
import '@dynamic-labs/rpc-providers';
import { useInternalDynamicContext } from '../../context/DynamicContext/useInternalDynamicContext.js';
import '../../context/ErrorContext/ErrorContext.js';
import '../../context/AccessDeniedContext/AccessDeniedContext.js';
import 'react-dom';
import '../../components/ShadowDOM/ShadowDOM.js';
import '../../context/ThemeContext/ThemeContext.js';
import { Icon } from '../../components/Icon/Icon.js';
import 'react-focus-lock';
import '../../components/Transition/ZoomTransition/ZoomTransition.js';
import '../../components/Transition/SlideInUpTransition/SlideInUpTransition.js';
import '../../components/Transition/OpacityTransition/OpacityTransition.js';
import '../../components/IconButton/IconButton.js';
import '../../components/Alert/Alert.js';
import '@dynamic-labs/wallet-book';
import '../../components/Popper/Popper/Popper.js';
import '../../components/Popper/PopperContext/PopperContext.js';
import { SendBalancePageLayout } from '../../components/SendBalancePageLayout/SendBalancePageLayout.js';
import '../../components/MenuList/Dropdown/Dropdown.js';
import { useFetchCurrency } from '../../widgets/DynamicWidget/hooks/useFetchCurrency/useFetchCurrency.js';
import { TransactionStatusLayout } from '../../components/TransactionStatusLayout/TransactionStatusLayout.js';
import { TransactionStage } from './components/TransactionStage/TransactionStage.js';
import '../../context/MockContext/MockContext.js';
import '../../context/CaptchaContext/CaptchaContext.js';
import { usePromise } from '../../utils/hooks/usePromise/usePromise.js';
import '../../utils/hooks/useUserUpdateRequest/useUserUpdateRequest.js';

const SendBalanceView = ({ initialRecipientAddress = '', initialValue, onClickBack, onClickClose, onError, onSuccess, onDone, displayPoweredByDynamicFooter = false, }) => {
    const [stage, setStage] = useState('form');
    const { primaryWallet, network } = useInternalDynamicContext();
    const [value, setValue] = useState(initialValue !== null && initialValue !== void 0 ? initialValue : null);
    const [recipientAddress, setRecipientAddress] = useState(initialRecipientAddress);
    const transactionRef = useRef(null);
    if (!primaryWallet) {
        throw new DynamicError('Primary wallet is not available, send balance cannot be displayed');
    }
    const walletConnector = primaryWallet.connector;
    const { data: balance } = usePromise(() => walletConnector.getBalance(), [walletConnector]);
    const { data: provider } = usePromise(() => walletConnector.getWeb3Provider(), [walletConnector]);
    const evmNetworks = (walletConnector && walletConnector.evmNetworks) || [];
    const evmNetwork = evmNetworks.find((evmNetwork) => evmNetwork.chainId === network);
    const networkIcon = evmNetwork && (jsx("img", { src: evmNetwork === null || evmNetwork === void 0 ? void 0 : evmNetwork.iconUrls[0], alt: `${evmNetwork === null || evmNetwork === void 0 ? void 0 : evmNetwork.name} icon` }));
    const networkName = (evmNetwork === null || evmNetwork === void 0 ? void 0 : evmNetwork.vanityName) || (evmNetwork === null || evmNetwork === void 0 ? void 0 : evmNetwork.name);
    const ChainIconComponent = getChainIcon(primaryWallet.chain);
    const chainInfo = getChainInfo(primaryWallet.chain);
    const chainIcon = (jsx(Icon, Object.assign({ size: 'small' }, { children: jsx(ChainIconComponent, {}) })));
    const [currencySymbol = ''] = useFetchCurrency(primaryWallet.connector);
    const buildFormStage = () => (jsx(SendBalancePageLayout, { initialValues: {
            amount: value === null ? '' : formatBigNumber(value),
            recipient: recipientAddress,
        }, balance: balance, onClickBack: onClickBack, chain: primaryWallet.chain, networkName: networkName, networkIcon: networkIcon, currencySymbol: currencySymbol, isLoading: false, walletAddress: shortenWalletAddress(primaryWallet.address, 3, 3), walletName: primaryWallet.connector.name, onClickClose: onClickClose, displayPoweredByDynamicFooter: displayPoweredByDynamicFooter, onSubmit: (data) => {
            setValue(utils.parseEther(data.amount));
            setRecipientAddress(data.recipient);
            setStage('confirmation');
        } }));
    const buildTransactionStage = () => {
        if (!provider || value === null) {
            return null;
        }
        const transaction = {
            from: primaryWallet.address,
            to: recipientAddress,
            value,
        };
        return (jsx(TransactionStage, { walletConnector: walletConnector, provider: provider, transaction: transaction, title: 'Confirm transaction', onClickBack: () => setStage('form'), onError: onError, displayPoweredByDynamicFooter: displayPoweredByDynamicFooter, onSuccess: (transaction) => {
                transactionRef.current = transaction;
                setStage('success');
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(transactionRef.current);
            } }));
    };
    const buildSuccessState = () => {
        var _a;
        if (value === null)
            return null;
        return (jsx(TransactionStatusLayout, { amount: formatBigNumber(value), destinationAddress: recipientAddress, networkCurrency: currencySymbol, networkName: (_a = chainInfo === null || chainInfo === void 0 ? void 0 : chainInfo.blockchainName) !== null && _a !== void 0 ? _a : '', NetworkIcon: chainIcon, onClickClose: onClickClose, onDone: () => onDone === null || onDone === void 0 ? void 0 : onDone(), displayPoweredByDynamicFooter: displayPoweredByDynamicFooter }));
    };
    const getCurrentView = () => {
        switch (stage) {
            case 'form':
                return buildFormStage();
            case 'confirmation':
                return buildTransactionStage();
            case 'success':
                return buildSuccessState();
        }
    };
    return getCurrentView();
};

export { SendBalanceView };
